import React, { Fragment, useEffect } from "react";
import CustomText from "../../atomic/CustomText";
import CustomImageViewer from "../../molecular/CustomImageViewer";
import FileDetail from "../FileDetail";
import Button from "../../organism/Button";
import { useHistory } from "react-router-dom";
import "../../atomic/styles.css";
import RequiredFieldTooltip from "../RequiredFieldTooltip";
import moment from "moment";

interface DataType {
  logo?: string | File;
  [key: string]: any;
}

interface DetailRendererProps {
  data: DataType;
  groupFields: string[];
  hiddenFields: string[];
  roundedImage?: boolean;
  showLoadingIndication?: boolean;
  ignoreLogoHeading?: boolean;
  MainHeading?: string;
  isFile?: boolean;
  className?: string;
  approval?: boolean;
  approvalSectionUrl?: any;
  containerClass?: string;
  inlineFields?: boolean;
  obsecureFields?: string[];
  gridMode?: boolean;
  /** Renders below fields in the rounded-image layout (aligned with field columns). */
  actions?: React.ReactNode;
}

interface DetailItemProps {
  heading: string;
  value: string | File | any;
  isImage?: boolean;
  containerClass?: string;
  roundedImage?: boolean;
  ignoreLogoHeading?: boolean;
  showLoadingIndication?: boolean;
  isFile?: boolean;
  mainHeading?: string;
  obsecure?: boolean;
}

type LabelKeys =
  | "ssn"
  | "mrn"
  | "icd10"
  | "logo"
  | "email"
  | "mobilePhone"
  | "fullName"
  | "firstName"
  | "lastName"
  | "signPermission"
  | "address2"
  | "zipcode"
  | "cbsTests"
  | "dateOfBirth"
  | "testType"
  | "organizationName"
  | "patientInfoDemographicSheet"
  | "endoscopyReport"
  | "insuranceCard"
  | "currentPrescriptionMedication"
  | "currentPrescriptionMedications"
  | "clinicNotes"
  | "middleInitial"
  | "state"
  | "sexAtBirth"
  | "primaryDiagnosedICD10"
  | "additionalDiagnosedICD10"
  | "primaryInsurance"
  | "insuranceCompanyName"
  | "policyNumber"
  | "isAltheaDxNeuropsychotropicMedications"
  | "isIDgNeuropsychotropicMedicationsCurrent"
  | "isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement"
  | "nameOfFacility"
  | "dateOfCollection"
  | "specimenId"
  | "specialty"
  | "medicarePullDate"
  | "tumorSite"
  | "facilityType"
  | "isTelehealth"
  | "isPrimaryCutaneousMelanocyticNeoplasm"
  | "isExposedToRadiation"
  | "lymphNodeStatus"
  | "isReportPrame"
  | "isSentinelLymphNodeBiopsy"
  | "isClinicallyPalpableNodes"
  | "isIntrasitMetastasis"
  | "historyPhysicalExam"
  | "pathologySurgicalFindings"
  | "pathologyReport"
  | "tubeBarCode"
  | "notificationMedium"
  | "reportDeliveryMethod"
  | "dateOfDischarge"
  | "typeOfFacility"
  | "isTissueShipped"
  | "shipTracking"
  | "shipMethod"
  | "sampleLocationA"
  | "sampleLocationB"
  | "guarantorName"
  | "isConsideredForTherapy"
  | "orderSOF"
  | "orderSummary";

function DetailItem({
  heading,
  value,
  isImage,
  containerClass,
  showLoadingIndication,
  roundedImage,
  ignoreLogoHeading,
  isFile,
  mainHeading,
  obsecure,
}: Readonly<DetailItemProps>): JSX.Element {
  const getLogoLabel = (
    roundedImage?: boolean,
    ignoreLogoHeading?: boolean
  ): string => {
    if (roundedImage) {
      return ignoreLogoHeading ? "" : "User Detail";
    }
    return "";
  };

  const labels: Record<LabelKeys, string> = {
    logo: getLogoLabel(roundedImage, ignoreLogoHeading),
    email: "Email ID",
    mobilePhone: "Phone Number",
    fullName: "Full Name",
    firstName: "First Name",
    lastName: "Last Name",
    signPermission: "Sign Permission",
    sampleLocationA: "Sample Location A",
    sampleLocationB: "Sample Location B",
    guarantorName: "Guarantor Name",
    address2: "Address 2",
    zipcode: "Zip Code",
    cbsTests: "Castle Biosciences Tests",
    dateOfBirth: "Date Of Birth",
    testType: "Test Type",
    organizationName: "Organization Name",
    currentPrescriptionMedication:
      "Current Prescription Medications (Patient MAR)",
    pathologyReport: "Pathology Report",
    currentPrescriptionMedications:
      "Current Prescription Medications (Patient MAR)",
    insuranceCard: "Insurance Cards",
    clinicNotes: "Clinic Notes",
    patientInfoDemographicSheet: "Patient Info/Demographics sheet",
    endoscopyReport: "Endoscopy Report",
    middleInitial: "Middle Initial",
    state: "State/Province",
    sexAtBirth: "Sex at Birth",
    dateOfDischarge: "Date of discharge",
    typeOfFacility: "Type of facility",
    icd10: "ICD-10 Code",
    ssn: "SSN",
    mrn: "MRN",
    primaryDiagnosedICD10: "Primary Diagnosed ICD-10",
    additionalDiagnosedICD10: "Additional Diagnosed ICD-10s.",
    primaryInsurance:
      "Primary insurance co. name. (if insurance cards/billing face sheet is uploaded, this section can be left blank)",
    insuranceCompanyName: "Insurance Company Name",
    policyNumber: "Policy Number",
    isAltheaDxNeuropsychotropicMedications:
      "Select ALL the NeuroIDgenetix medications you are considering for new treatment and/or dosage change for this patient.",
    isIDgNeuropsychotropicMedicationsCurrent:
      "CURRENT PRESCRIPTION MEDICATIONS - Please select all current psychiatric and non-psychiatric prescription medications. (If patient MAR sheet is uploaded, this section can be left blank)",
    isIDgOTCHerbalMedsEnvironmentalFactorDietarySupplement:
      "LIFESTYLE FACTORS, OTC MEDICATIONS & OTHER - Please select all that the patient regularly takes or uses any of the items below",
    nameOfFacility: "Name of facility where tissue is maintained",
    dateOfCollection: "Date of Collection",
    specimenId: "Specimen ID",
    medicarePullDate: "Medicare Pull Date",
    tumorSite: "Tumor Site",
    facilityType: "Location of Sample Collection",
    isTelehealth: "Telehealth Order",
    tubeBarCode: "Tube Barcode",
    isPrimaryCutaneousMelanocyticNeoplasm:
      "I attest the specimen being submitted for testing is a primary cutaneous melanocytic neoplasm for which the diagnosis is uncertain, (despite the performance of standard-of-care test procedures and relevant ancillary tests), the patient may be subjected to additional intervention as a result of the diagnostic uncertainty, and this patient was not receiving immunosuppressant or radiation therapy at the time of biopsy.",
    isExposedToRadiation:
      "Has the tissue in this sample been exposed to radiation?",
    lymphNodeStatus: "Lymph Node Status",
    isReportPrame: "Add-On Testing: DecisionDx-Prame?",
    isSentinelLymphNodeBiopsy:
      "Has the patient had a sentinel lymph node biopsy for this melanoma?",
    isClinicallyPalpableNodes:
      "Does/Did this patient have clinically palpable nodes?",
    isIntrasitMetastasis: "Does/Did this patient have intransit metastasis?",
    historyPhysicalExam: "History and Physical Exam",
    pathologySurgicalFindings: "Surgical and Pathology Findings",
    notificationMedium: "Notification Medium",
    reportDeliveryMethod: "Report Delivery Method",
    specialty: "Speciality",
    isTissueShipped: "Sample shipped - No shipping airbill or material request needed.",
    shipTracking: "Shipment Tracking Number",
    shipMethod: "Shipment Method",
    isConsideredForTherapy: "Is this patient currently being considered for FDA-approved systemic therapy to treat atopic dermatitis, and do you intend to use the results of this molecular test to help inform therapy selection?*",
    orderSOF: "Signature on file ( SOF )",
    orderSummary: "Order Summary",
  };

  const headingName =
    ignoreLogoHeading && heading === "logo"
      ? ""
      : labels[heading as LabelKeys] || heading;

  const getValueText = (value: any, mainHeading: any, headingName: any) => {
    // if (headingName === 'Notification Medium' || headingName === 'Report Delivery Method') {
    //   return (
    //     <div className="flex flex-row gap-4">
    //       {value?.map((item: any, index: number) => (

    //         <RequiredFieldTooltip tooltipText={`Get notified via ${item.name}`} id={`${item.name}`} required>
    //         <label
    //           key={index}
    //           className="checkboxContainer mr-3 block"
    //         >
    //           <input
    //             type="checkbox"
    //             checked={item.value}
    //             readOnly
    //             className="focus:ring-primary text-primary cursor-not-allowed before:content-[' ']"
    //           />
    //           <span>{item.name}</span>
    //           <span className="cursor-not-allowed checkmark" />
    //         </label>
    //           </RequiredFieldTooltip>
    //       ))}
    //     </div>
    //   );
    // }
    if (
      headingName === "Sex at Birth" ||
      headingName === "Gender" ||
      headingName === "gender"
    ) {
      return <div className="capitalize">{value}</div>;
    }
    if (typeof value === "string") {
      if (!value) {
        return mainHeading === "Patient Information"
          ? "[Pending Entry]"
          : "---";
      }
      if (value === "N/A") return `I Don't Know`;
      if (headingName === "Test Type") {
        const segments = value.includes(" / ")
          ? value.split(" / ").map((s) => s.trim()).filter(Boolean)
          : [value];
        if (segments.length > 0) {
          return (
            <div className="flex flex-col gap-1">
              {segments.map((segment, index) => {
                const colonIndex = segment.indexOf(":");
                const title = colonIndex >= 0 ? segment.slice(0, colonIndex).trim() : segment;
                const description = colonIndex >= 0 ? segment.slice(colonIndex + 1).trim() : "";
                return (
                  <div key={`test-type-${title}-${index}`} className="font-sans text-royalBlue">
                    <span className="font-semibold">{title}</span>
                    {description ? `: ${description}` : ""}
                  </div>
                );
              })}
            </div>
          );
        }
      }
      return value;
    }
    if (headingName === 'Sample shipped - No shipping airbill or material request needed.') {
      return <div>{value ? 'Yes' : 'No'}</div>
    }

    if (Array.isArray(value)) {
      return value.join(", ");
    }

    return "---";
  };
  const updatedAttributes: any = {};
  if (obsecure) {
    // updatedAttributes["data-openreplay-obscured"] = true;
    updatedAttributes["data-dd-privacy"] = "mask";
  }
  return (
    <div
      {...updatedAttributes}
      className={`xs:mt-5 md:my-5 text-royalBlue ${containerClass} ${isFile ? "align-top" : ""
        }`}
    >
      <CustomText
        text={headingName}
        fontWeight="font-semibold"
        fontSize={headingName === "User Detail" ? "text-sm" : "text-xs"}
        className={`mb-2 ${roundedImage && "mt-2"} ${headingName === "npi"
          ? "uppercase"
          : headingName === "Castle Biosciences Tests" ||
            headingName === "Email ID" ||
            headingName === "Phone Number" ||
            headingName === "State/Province" ||
            headingName === "SSN" ||
            headingName === "MRN"
            ? "normalize"
            : "capitalizeFirstWord"
          } `}
      />
      {isImage ? (
        <CustomImageViewer
          imageTitle="Logo"
          imageUrl={value instanceof File ? URL.createObjectURL(value) : value}
          className={`mb-1 ${roundedImage && "md:mt-6 mb-6"}`}
          roundedImage={roundedImage}
        />
      ) : isFile ? (
        Array.isArray(value) ? (
          value.length > 0 ? (
            <div className="flex flex-wrap">
              {value.map((file, index) => (
                <FileDetail key={index} doc={file} hideRemove={true} />
              ))}
            </div>
          ) : (
            <div>---</div>
          )
        ) : value ? (
          <FileDetail doc={value} hideRemove={true} />
        ) : (
          <div>---</div>
        )
      ) : (
        <div>{getValueText(value, mainHeading, headingName)}</div>
      )}
    </div>
  );
}

function DetailRenderer({
  data,
  groupFields = [],
  hiddenFields = [],
  showLoadingIndication,
  roundedImage,
  ignoreLogoHeading,
  MainHeading,
  isFile,
  className,
  approval,
  approvalSectionUrl,
  containerClass,
  obsecureFields,
  inlineFields = false,
  gridMode = false,
  actions,
}: Readonly<DetailRendererProps>): JSX.Element {
  const history = useHistory();
  const renderDetailItem = (key: string) => {
    if (hiddenFields.includes(key)) return null;

    const getContainerClass = () => {
      if (inlineFields) return `inline-block mr-4 ${containerClass}`;
      // In gridMode, we do NOT want inline-block styling as the parent grid controls layout
      if (groupFields.includes(key) && !gridMode)
        return `inline-block mr-7 my-0 ${containerClass}`;
      return containerClass || "";
    };

    if (key === "dateOfBirth") {
      return (
        <DetailItem
          obsecure={obsecureFields?.includes(key)}
          key={key}
          heading={key}
          showLoadingIndication={showLoadingIndication}
          value={moment(data[key])?.format?.("MM-DD-YYYY")}
          isImage={false}
          containerClass={getContainerClass()}
          roundedImage={roundedImage}
          ignoreLogoHeading={ignoreLogoHeading}
          isFile={data[key] instanceof File || isFile || !!data[key]?.filename}
          mainHeading={MainHeading}
        />
      );
    }

    if (
      key === "patientInfoDemographicSheet" ||
      key === "insuranceCard" ||
      key === "currentPrescriptionMedication" ||
      key === "pathologyReport" ||
      key === "endoscopyReport" ||
      key === "clinicNotes" ||
      key === "orderSOF" ||
      key === "orderSummary"
    ) {
      return (
        <DetailItem
          key={key}
          obsecure={obsecureFields?.includes(key)}
          heading={key}
          showLoadingIndication={showLoadingIndication}
          value={data[key]}
          isImage={key.includes("logo")}
          containerClass={getContainerClass()}
          roundedImage={roundedImage}
          ignoreLogoHeading={ignoreLogoHeading}
          isFile={true}
          mainHeading={MainHeading}
        />
      );
    }
    return (
      <DetailItem
        key={key}
        obsecure={obsecureFields?.includes(key)}
        heading={key}
        showLoadingIndication={showLoadingIndication}
        value={
          typeof data[key] === "number"
            ? data[key].toString()
            : Array.isArray(data[key])
              ? data[key].join(",")
              : data[key]
        }
        isImage={key.includes("logo")}
        containerClass={getContainerClass()}
        roundedImage={roundedImage}
        ignoreLogoHeading={ignoreLogoHeading}
        isFile={data[key] instanceof File || isFile || !!data[key]?.filename}
        mainHeading={MainHeading}
      />
    );
  };

  const renderFields = () => {
    // Start with fields specified in groupFields that actually exist in data
    // Use simple lookup to be robust against prototype/proxy issues
    const orderedGroupFields = groupFields.filter(field => data[field] !== undefined);

    // Get all other fields in data that are NOT in groupFields
    const remainingFields = Object.keys(data).filter(key => !groupFields.includes(key));

    // Combine them: grouped fields first (in order), then remaining fields
    const allFields = [...orderedGroupFields, ...remainingFields];

    // Filter out hidden fields
    const fieldsToRender = allFields.filter(key => !hiddenFields.includes(key));

    if (inlineFields) {
      return (
        <div className="flex flex-wrap gap-4">
          {fieldsToRender.map(renderDetailItem)}
        </div>
      );
    }

    return fieldsToRender.map(renderDetailItem);
  };

  return (
    <div className={`${className} ${approval && "w-full"}`}>
      {roundedImage ? (
        <div
          className={`md:grid md:grid-cols-twoColumnLayout ${
            ignoreLogoHeading ? "place-items-center" : "md:items-start"
          }`}
        >
          <div>{renderDetailItem("logo")}</div>
          <div
            className={` md:ml-7 ${ignoreLogoHeading ? "-mt-8" : "md:my-5 md:mt-12"
              }`}
          >
            {Object.keys(data).map(
              (key) => key !== "logo" && renderDetailItem(key)
            )}
            {actions}
          </div>
        </div>
      ) : MainHeading ? (
        <div>
          <div className={`${approval && "flex justify-between items-center"}`}>
            <CustomText
              text={MainHeading}
              fontWeight="font-semibold"
              className="my-2 mt-5"
            />
            {approval && (
              <Button
                textInformation={{
                  text: "edit",
                  fontWeight: "font-semibold",
                }}
                loading={false}
                disabled={false}
                onClick={() => history.push(approvalSectionUrl)}
                buttonTheme="secondary"
                className="mt-3 bg-white"
              />
            )}
          </div>
          {renderFields()}
        </div>
      ) : (
        renderFields()
      )}
    </div>
  );
}

export default DetailRenderer;
